/**
 * Link AUSTIN Case Modal
 *
 * Lets a user link the current Navigator session to an existing AUSTIN case by
 * entering either a Case ID (e.g. ABE2-26-28) or pasting a full AUSTIN case URL.
 * Mirrors the AUSTIN autofill auto-link: it writes `case_id` + `AUSTIN_LINK` into
 * session metadata via the same endpoint, so the backend stamps who/when linked.
 */

import React, { useState } from 'react';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Button from '@amzn/meridian/button';
import Input from '@amzn/meridian/input';
import Text from '@amzn/meridian/text';
import Alert from '@amzn/meridian/alert';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import { updateSessionMetadata } from '../../../store/sessionSlice';
import { useAppDispatch } from '../../../store/store';
import { showSuccess, showError } from '../../../store/toastsSlice';
import { normalizeCaseId } from '../../../utils/austin';
import { linkInputsToCaseLink } from './dataTransformations';
import { METADATA_KEYS } from '../../../constants';

const MODAL_WIDTH = 480;

interface LinkAustinCaseModalProps {
  open: boolean;
  sessionId: string;
  currentMetadata?: Record<string, any> | null;
  onClose: () => void;
}

const LinkAustinCaseModal: React.FC<LinkAustinCaseModalProps> = ({
  open,
  sessionId,
  currentMetadata,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const [caseIdInput, setCaseIdInput] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const reset = () => {
    setCaseIdInput('');
    setUrlInput('');
    setError(null);
    setIsSubmitting(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleLink = async () => {
    setError(null);

    const link = linkInputsToCaseLink(caseIdInput, urlInput);
    if (link.error !== null) {
      setError(link.error);
      return;
    }
    const { caseId, austinLink } = link;

    setIsSubmitting(true);
    try {
      await dispatch(
        updateSessionMetadata({
          sessionId,
          metadata: {
            ...(currentMetadata || {}),
            [METADATA_KEYS.CASE_ID]: caseId,
            [METADATA_KEYS.AUSTIN_LINK]: austinLink,
          },
        }),
      ).unwrap();
      dispatch(showSuccess(`AUSTIN Case ${caseId} linked successfully`));
      handleClose();
    } catch (err: any) {
      setIsSubmitting(false);
      const message = typeof err === 'string' ? err : 'Failed to link AUSTIN case';
      setError(message);
      dispatch(showError(message));
    }
  };

  return (
    <Modal
      title="Link AUSTIN Case"
      open={open}
      width={MODAL_WIDTH}
      onClose={handleClose}
      scrollContainer="viewport"
      closeLabel="Cancel"
    >
      <Column spacing="300">
        <div>
          <Text type="h100">Enter AUSTIN Case ID or Case URL</Text>
          <Text type="b200" color="secondary">
            You can enter either the Case ID or paste the full link from AUSTIN.
          </Text>
        </div>

        <Input
          label="AUSTIN Case ID"
          type="text"
          value={caseIdInput}
          onChange={(value) => setCaseIdInput(normalizeCaseId(value))}
          placeholder="Enter Case ID (e.g., ABE2-26-28)"
          size="medium"
        />

        <Text type="b200" color="secondary" alignment="center">
          or
        </Text>

        <Input
          label="AUSTIN Case URL"
          type="url"
          value={urlInput}
          onChange={setUrlInput}
          placeholder="Paste full AUSTIN case URL"
          size="medium"
        />

        <Text type="b100" color="secondary">
          Example: https://na-gamma.austin.a2z.com/incidents/group/ABE2-26-GROUP-19/incident/ABE2-26-28/summary
        </Text>

        {error && (
          <Alert type="error" size="small">
            {error}
          </Alert>
        )}
      </Column>

      <ModalFooter>
        <Row alignmentHorizontal="end" spacing="300" widths="fit">
          <Button type="secondary" size="small" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="primary" size="small" onClick={handleLink} disabled={isSubmitting}>
            Link Case
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};

export default LinkAustinCaseModal;
